import { authedFetch } from './http';

// Mirrors the backend `profileView` (profile_handlers.go). `newUser` drives the
// onboarding gate: 1 = ready, 0 = needs the welcome-back terms re-accept.
export type Profile = {
  nickname: string;
  avatarCap?: string;
  avatarUrl?: string;
  canton?: string;
  champion?: string;
  bio?: string;
  newUser: number;
  autoPick: boolean;
};

export type ProfilePatch = {
  nickname: string;
  avatarCap?: string;
  canton?: string;
  champion?: string;
  bio?: string;
};

type Auth = { getToken: () => string | null; hydrate: () => Promise<void> };

/** GET /api/profile — returns null when the user has no profile yet (404). */
export async function getProfile({ getToken, hydrate }: Auth): Promise<Profile | null> {
  try {
    return await authedFetch<Profile>(getToken, hydrate, '/api/profile');
  } catch (e) {
    if ((e as { status?: number }).status === 404) return null;
    throw e;
  }
}

/** PUT /api/profile — throws ApiError with code `nickname_taken` (409) etc. */
export async function saveProfile({ getToken, hydrate }: Auth, patch: ProfilePatch): Promise<Profile> {
  return authedFetch<Profile>(getToken, hydrate, '/api/profile', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

/** GET /api/profile/check-nickname?n=… */
export async function checkNickname(
  { getToken, hydrate }: Auth,
  n: string,
): Promise<{ available: boolean; reason?: string }> {
  return authedFetch(getToken, hydrate, `/api/profile/check-nickname?n=${encodeURIComponent(n)}`);
}

/** POST /api/me/onboarding/light — acknowledge the welcome-back terms re-accept. */
export async function acknowledgeWelcomeBack({ getToken, hydrate }: Auth): Promise<void> {
  await authedFetch(getToken, hydrate, '/api/me/onboarding/light', { method: 'POST' });
}

/** PUT /api/me/preferences — toggle server-side auto-pick. */
export async function setAutoPick({ getToken, hydrate }: Auth, autoPick: boolean): Promise<void> {
  await authedFetch(getToken, hydrate, '/api/me/preferences', {
    method: 'PUT',
    body: JSON.stringify({ autoPick }),
  });
}

/** POST /api/profile/avatar (multipart `file`) → new avatar URL. */
export async function uploadAvatar({ getToken, hydrate }: Auth, file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  // Note: no Content-Type header — the browser sets the multipart boundary.
  const body = await authedFetch<{ avatarUrl: string }>(getToken, hydrate, '/api/profile/avatar', {
    method: 'POST',
    body: fd,
  });
  return body.avatarUrl;
}

/** DELETE /api/profile/avatar. */
export async function deleteAvatar({ getToken, hydrate }: Auth): Promise<void> {
  await authedFetch(getToken, hydrate, '/api/profile/avatar', { method: 'DELETE' });
}
